/**
 * eBay description composer — builds the "CardSimp default" HTML in code,
 * branched by product type so terminology actually matches what was listed.
 *
 * Buckets:
 *   - slab       (graded card; grading_company set or product_type=slab)
 *   - sealed     (sealed product; sealed_condition set or product_type sealed/booster/etb)
 *   - lot        (multi-card lot/bulk/mixed)
 *   - accessory  (sleeves, binders, deck boxes, etc.)
 *   - single     (raw single card — default)
 *
 * eBay-policy posture: no platform guarantees on shipping/returns/authenticity/grading;
 * photos are the source of truth; one neutral "Description prepared with CardSimp" credit.
 */

export type DescriptionBucket = 'single' | 'slab' | 'sealed' | 'lot' | 'accessory';

export interface ComposerItem {
  title?: string | null;
  game?: string | null;
  set_name?: string | null;
  set_code?: string | null;
  card_number?: string | null;
  rarity?: string | null;
  finish?: string | null;
  language?: string | null;
  condition?: string | null;
  slab_grade?: string | number | null;
  grading_company?: string | null;
  certification_number?: string | null;
  description?: string | null;
  is_first_edition?: boolean | null;
  is_reverse_holo?: boolean | null;
  sealed_condition?: string | null;
  category?: string | null;
  product_type?: string | null;
  quantity?: number | null;
}

/** Sentinel stored in `ebay_description_templates.body_html` for the CardSimp default row. */
export const CARDSIMP_COMPOSER_SENTINEL = '<!-- CARDSIMP_COMPOSER -->';

export function isCardSimpComposerTemplate(bodyHtml: string | null | undefined): boolean {
  return !!bodyHtml && bodyHtml.trim().startsWith(CARDSIMP_COMPOSER_SENTINEL);
}

const GAME_LABELS: Record<string, string> = {
  pokemon: 'Pokémon',
  mtg: 'Magic: The Gathering',
  magic: 'Magic: The Gathering',
  yugioh: 'Yu-Gi-Oh!',
  'yu-gi-oh': 'Yu-Gi-Oh!',
  lorcana: 'Lorcana',
  onepiece: 'One Piece',
  'one-piece': 'One Piece',
  flesh_and_blood: 'Flesh and Blood',
  'flesh-and-blood': 'Flesh and Blood',
  star_wars_unlimited: 'Star Wars Unlimited',
  'star-wars-unlimited': 'Star Wars Unlimited',
  digimon: 'Digimon',
  sports: 'Sports Cards',
};

export function formatGame(g: string | null | undefined): string {
  if (!g) return '';
  const raw = String(g).trim();
  const k1 = raw.toLowerCase();
  const k2 = k1.replace(/_/g, '-');
  if (GAME_LABELS[k1]) return GAME_LABELS[k1];
  if (GAME_LABELS[k2]) return GAME_LABELS[k2];
  return raw.split(/[\s_-]+/).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

const CONDITION_LABELS: Record<string, string> = {
  near_mint: 'Near Mint',
  lightly_played: 'Lightly Played',
  moderately_played: 'Moderately Played',
  heavily_played: 'Heavily Played',
  damaged: 'Damaged',
  mint: 'Mint',
  good: 'Good',
  acceptable: 'Acceptable',
};

function prettyCondition(c?: string | null): string {
  if (!c) return '';
  const k = String(c).toLowerCase();
  if (CONDITION_LABELS[k]) return CONDITION_LABELS[k];
  return String(c).replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

const SEALED_LABELS: Record<string, string> = {
  factory_sealed: 'Factory Sealed',
  fs: 'Factory Sealed',
  'fs-': 'Factory Sealed (minor shelf wear)',
  'fs--': 'Factory Sealed (visible shelf wear)',
  near_sealed: 'Near Sealed',
  resealed: 'Resealed (disclosed)',
};

function prettySealed(c?: string | null): string {
  if (!c) return '';
  const k = String(c).toLowerCase();
  if (SEALED_LABELS[k]) return SEALED_LABELS[k];
  return String(c).replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
}

function escapeHtml(s: string): string {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function detectBucket(item: ComposerItem): DescriptionBucket {
  const pt = String(item.product_type || '').toLowerCase();
  const cat = String(item.category || '').toLowerCase();
  const blob = `${pt} ${cat}`;

  if (pt === 'slab' || pt === 'graded' || item.grading_company) return 'slab';
  if (
    pt === 'sealed' ||
    pt === 'booster_box' ||
    pt === 'booster_pack' ||
    pt === 'etb' ||
    pt === 'elite_trainer_box' ||
    pt === 'collection_box' ||
    pt === 'bundle' ||
    pt === 'tin' ||
    pt === 'theme_deck' ||
    pt === 'starter_deck' ||
    blob.includes('sealed') ||
    item.sealed_condition
  ) {
    return 'sealed';
  }
  if (pt === 'lot' || pt === 'bulk' || pt === 'mixed' || blob.includes('lot') || blob.includes('bulk') || blob.includes('mixed')) {
    return 'lot';
  }
  if (pt === 'accessory' || blob.includes('accessor') || blob.includes('sleeve') || blob.includes('binder') || blob.includes('deck_box')) {
    return 'accessory';
  }
  return 'single';
}

// ============ Section builders ============

function renderCreditStrip(): string {
  return `<div style="background:#fffbe6;border:1px solid #f0d000;border-radius:6px;padding:10px 14px;margin:0 0 18px;font-size:13px;color:#5a4500;text-align:center;">Description prepared with CardSimp.</div>`;
}

function renderHeader(item: ComposerItem): string {
  const title = escapeHtml(item.title || 'Listing');
  const game = formatGame(item.game);
  const set = item.set_name || item.set_code || '';
  const num = item.card_number ? `#${item.card_number}` : '';
  const metaParts = [game, set, num].filter(Boolean).map(escapeHtml);
  const meta = metaParts.length
    ? `<div style="margin:0 0 16px;font-size:13px;color:#666;">${metaParts.join(' &middot; ')}</div>`
    : '';
  return `<h2 style="margin:0 0 6px;font-size:22px;font-weight:700;color:#111;line-height:1.25;">${title}</h2>${meta}`;
}

interface SpecRow {
  label: string;
  value: string;
}

function specsRowsForBucket(bucket: DescriptionBucket, item: ComposerItem): SpecRow[] {
  const rows: SpecRow[] = [];
  const push = (label: string, value?: string | number | null) => {
    if (value === null || value === undefined) return;
    const s = String(value).trim();
    if (!s) return;
    rows.push({ label, value: s });
  };

  // Universal identity rows
  push('Game', formatGame(item.game));
  push('Set', item.set_name || item.set_code || '');
  push('Card #', item.card_number);
  push('Language', item.language);

  if (bucket === 'slab') {
    // Graded: grader + grade + cert. No raw condition / finish / variant / edition.
    const grader = item.grading_company || '';
    const grade = item.slab_grade != null && String(item.slab_grade).trim() !== '' ? String(item.slab_grade) : '';
    if (grader || grade) {
      push('Grader', [grader, grade].filter(Boolean).join(' '));
    }
    push('Cert #', item.certification_number);
  } else if (bucket === 'sealed') {
    // Sealed: no card-specific rows beyond identity.
    push('Sealed condition', prettySealed(item.sealed_condition));
  } else {
    // single / lot / accessory: card-condition vocab applies
    push('Rarity', item.rarity);
    push('Finish', item.finish);
    if (item.is_reverse_holo) push('Variant', 'Reverse Holo');
    if (item.is_first_edition) push('Edition', '1st Edition');
    push('Condition', prettyCondition(item.condition));
  }

  if ((item.quantity ?? 1) > 1) {
    push('Quantity', String(item.quantity));
  }

  return rows;
}

function renderSpecsTable(bucket: DescriptionBucket, item: ComposerItem): string {
  const rows = specsRowsForBucket(bucket, item);
  if (!rows.length) return '';
  const trs = rows
    .map(
      (r) =>
        `<tr><td style="padding:3px 12px 3px 0;color:#666;width:130px;">${escapeHtml(r.label)}</td><td style="padding:3px 0;">${escapeHtml(r.value)}</td></tr>`
    )
    .join('');
  return `<div style="background:#fafafa;border:1px solid #ececec;border-radius:6px;padding:14px 16px;margin:0 0 18px;"><div style="font-weight:600;font-size:14px;color:#222;margin:0 0 8px;">Listing details</div><table style="border-collapse:collapse;width:100%;font-size:13px;">${trs}</table></div>`;
}

function renderSellerNote(item: ComposerItem): string {
  const note = (item.description || '').trim();
  if (!note) return '';
  return `<div style="margin:0 0 18px;padding:12px 14px;border-left:3px solid #f0d000;background:#fffdf2;font-size:14px;color:#333;"><div style="font-weight:600;margin-bottom:4px;color:#222;font-size:13px;">Seller notes</div>${escapeHtml(note)}</div>`;
}

function renderIntro(bucket: DescriptionBucket, item: ComposerItem): string {
  const title = escapeHtml(item.title || 'this listing');
  let heading = '';
  let body = '';
  let bullets: string[] = [];

  switch (bucket) {
    case 'slab':
      heading = `This is ${title} &mdash; photos are the source of truth`;
      body = `Please review every photo of the slab carefully before buying. Case scratches, label imperfections, scuffs, and any slab cosmetic characteristics that may not be fully captured in words are shown in the images.`;
      bullets = [
        'By purchasing, the buyer acknowledges they reviewed the photos.',
        'The slab is sold as shown in the photos.',
        'Any visible case wear or label characteristics are considered disclosed via the images.',
      ];
      break;
    case 'sealed':
      heading = `This is ${title} &mdash; photos are the source of truth`;
      body = `Please review every photo of the outer packaging carefully before buying. Shelf wear, edge wear, dings, plastic-wrap condition, and any other outer characteristics that may not be fully captured in words are shown in the images.`;
      bullets = [
        'By purchasing, the buyer acknowledges they reviewed the photos.',
        'The product is sold as shown in the photos.',
        'No claims are made about pull rates, hits, print runs, or contents.',
      ];
      break;
    case 'lot':
      heading = `This is ${title} &mdash; photos show representative quality`;
      body = `Please review every photo carefully before buying. Not every card is individually photographed; the images show overall quality and representative pieces.`;
      bullets = [
        'By purchasing, the buyer acknowledges they reviewed the photos.',
        'The lot is sold as shown in the photos.',
        'All cards in the lot are of comparable condition to those shown unless otherwise noted.',
      ];
      break;
    case 'accessory':
      heading = `This is ${title} &mdash; photos are the source of truth`;
      body = `Please review every photo carefully before buying. Any wear, marks, or characteristics that may not be fully captured in words are shown in the images.`;
      bullets = [
        'By purchasing, the buyer acknowledges they reviewed the photos.',
        'The item is sold as shown in the photos.',
      ];
      break;
    case 'single':
    default:
      heading = `This is ${title} &mdash; photos are the source of truth`;
      body = `Please review every photo carefully before buying. Any marks, wear, edge issues, centering, surface texture, print lines, or condition characteristics that may not be fully captured by words are shown in the images.`;
      bullets = [
        'By purchasing, the buyer acknowledges they reviewed the photos.',
        'The card is sold as shown in the photos.',
        'Any visible wear or imperfections are considered disclosed via the images.',
      ];
      break;
  }

  const lis = bullets.map((b) => `<li>${b}</li>`).join('');
  return `<h3 style="margin:18px 0 8px;font-size:15px;font-weight:600;color:#111;">${heading}</h3><p style="margin:0 0 10px;">${body}</p><ul style="margin:0 0 16px;padding-left:20px;">${lis}</ul>`;
}

function renderMultiUnitDisclaimer(bucket: DescriptionBucket, qty: number): string {
  if (!qty || qty <= 1) return '';
  let copy = '';
  switch (bucket) {
    case 'sealed':
      copy = `This listing is for ${qty} sealed units. Photos may show a representative unit; all units offered are the same product and were stored together. Outer condition can vary slightly between units within normal manufacturer tolerances &mdash; see photos for the representative example.`;
      break;
    case 'slab':
      copy = `This listing covers ${qty} graded slabs of the same card. Each slab is its own item with its own grade label, case, and surface &mdash; minor cosmetic differences between slabs are normal. Buyer receives one slab per quantity purchased.`;
      break;
    case 'lot':
      copy = `This is a multi-card lot of ${qty} items. Not every card is individually photographed; photos show overall quality and representative pieces. All cards in the lot are of comparable condition to those shown unless otherwise noted.`;
      break;
    case 'accessory':
      copy = `This listing covers ${qty} units of the same accessory. Photos show a representative unit; remaining units are stored together and are of comparable condition.`;
      break;
    case 'single':
    default:
      copy = `This listing covers ${qty} units. Not every unit is individually photographed &mdash; additional units are of comparable quality to those shown, but minor card-to-card variation in centering, surface, and corners is normal. Buyer receives one unit per quantity purchased.`;
      break;
  }
  return `<div style="margin:0 0 18px;padding:12px 14px;border-left:3px solid #f0d000;background:#fffdf2;font-size:14px;color:#333;"><div style="font-weight:600;margin-bottom:4px;color:#222;font-size:13px;">Multiple units</div>${copy}</div>`;
}

function renderConditionVocab(bucket: DescriptionBucket): string {
  // Only raw-card buckets get the NM/LP/MP/HP/DMG table.
  if (bucket !== 'single' && bucket !== 'lot') return '';
  return `<h3 style="margin:18px 0 8px;font-size:15px;font-weight:600;color:#111;">Condition vocabulary</h3><p style="margin:0 0 8px;font-size:13px;color:#555;">Standard hobby terminology used in this listing. Photos take precedence over wording.</p><table style="border-collapse:collapse;width:100%;font-size:13px;margin:0 0 18px;"><tr style="background:#fafafa;"><td style="padding:6px 10px;border:1px solid #ececec;width:110px;font-weight:600;">NM &mdash; Near Mint</td><td style="padding:6px 10px;border:1px solid #ececec;">Minimal handling, sharp corners, clean surface under normal inspection.</td></tr><tr><td style="padding:6px 10px;border:1px solid #ececec;font-weight:600;">LP &mdash; Lightly Played</td><td style="padding:6px 10px;border:1px solid #ececec;">Minor wear visible on close inspection.</td></tr><tr style="background:#fafafa;"><td style="padding:6px 10px;border:1px solid #ececec;font-weight:600;">MP &mdash; Moderately Played</td><td style="padding:6px 10px;border:1px solid #ececec;">Clear handling wear, edge whitening, or surface marks visible.</td></tr><tr><td style="padding:6px 10px;border:1px solid #ececec;font-weight:600;">HP &mdash; Heavily Played</td><td style="padding:6px 10px;border:1px solid #ececec;">Significant wear; remains identifiable and intact.</td></tr><tr style="background:#fafafa;"><td style="padding:6px 10px;border:1px solid #ececec;font-weight:600;">DMG &mdash; Damaged</td><td style="padding:6px 10px;border:1px solid #ececec;">Bends, creases, tears, water damage, or similar structural issues.</td></tr></table>`;
}

function renderGradedDisclosure(bucket: DescriptionBucket): string {
  if (bucket !== 'slab') return '';
  return `<h3 style="margin:18px 0 8px;font-size:15px;font-weight:600;color:#111;">Third-party grading &amp; slab disclosure</h3><ul style="margin:0 0 16px;padding-left:20px;"><li>The grade reflects the grading company&rsquo;s opinion at the time of grading, not a guarantee by the seller. Subgrades, centering opinions, and eye appeal are subjective.</li><li>No claims are made about crossover results, regrade outcomes, potential bumps, or future resale value.</li><li>Slab case cosmetics (light scratches, scuffs, hairlines, label placement, inner-well marks) are considered normal and are shown in the photos.</li><li>To the seller&rsquo;s knowledge the slab is authentic and has not been tampered with. Buyers are encouraged to verify the cert number directly on the grading company&rsquo;s website before purchase.</li><li>Because the grade and slab condition are visible in the photos and cert lookup, returns based on disagreement with the assigned grade, subgrades, or slab cosmetics shown in the photos are not accepted.</li><li>Cracked slabs, reholders, or any post-grading modification are disclosed explicitly in the seller notes when applicable &mdash; otherwise the slab is offered in its original graded state.</li></ul>`;
}

function renderSingleCardDisclosure(bucket: DescriptionBucket): string {
  if (bucket !== 'single') return '';
  return `<h3 style="margin:18px 0 8px;font-size:15px;font-weight:600;color:#111;">Raw single card disclosure</h3><ul style="margin:0 0 16px;padding-left:20px;"><li>Condition grade (NM/LP/MP/HP/DMG) is the seller&rsquo;s honest opinion using standard hobby vocabulary. It is not a third-party grade and does not predict any future professional grade.</li><li>Centering, print lines, roller marks, holo scratches, edge whitening, and surface texture are inherent to the print run and are shown in the photos. They are considered disclosed via the images even if not called out in text.</li><li>Photos are taken under normal lighting. Scans, angled shots, and bright light can reveal characteristics that everyday handling would not; the card is described as it appears under normal viewing.</li><li>Front and back are shown. Any characteristic visible in the photos (including minor ones) is part of what is being sold.</li><li>To the seller&rsquo;s knowledge the card is authentic. No claims are made about future value, pop reports, print rarity beyond what is officially published, or grading potential.</li><li>Because condition is shown in the photos, returns based on centering, minor surface characteristics, or disagreement with the seller&rsquo;s condition grade when the photos accurately depict the card are not accepted.</li></ul>`;
}

function renderSealedDisclosure(bucket: DescriptionBucket): string {
  if (bucket !== 'sealed') return '';
  return `<h3 style="margin:18px 0 8px;font-size:15px;font-weight:600;color:#111;">Sealed product disclosure</h3><ul style="margin:0 0 16px;padding-left:20px;"><li>Contents have not been inspected; outer condition is shown in photos.</li><li>No claims are made about pull rates, hits, print runs, or contents.</li><li>To the seller's knowledge, the product has not been resealed or tampered with.</li><li>If the seller has reason to believe a product was previously opened, it is not knowingly listed as sealed.</li></ul>`;
}

function renderLotDisclosure(bucket: DescriptionBucket): string {
  if (bucket !== 'lot') return '';
  return `<h3 style="margin:18px 0 8px;font-size:15px;font-weight:600;color:#111;">Lot disclosure</h3><ul style="margin:0 0 16px;padding-left:20px;"><li>Photos show representative cards and overall quality of the lot.</li><li>Not every card is individually photographed.</li><li>Cards are sold as-is in the configuration shown.</li></ul>`;
}

function renderFooter(): string {
  return `<h3 style="margin:18px 0 8px;font-size:15px;font-weight:600;color:#111;">Shipping, returns &amp; payment</h3><p style="margin:0 0 16px;">Shipping speed, packaging, return window, and payment terms are set by the seller in the eBay listing fields above. Payment is processed through eBay checkout. Any seller-specific guarantees, return windows, or shipping upgrades are defined there &mdash; this description does not modify or override them.</p><div style="background:#f7f7f7;border-radius:6px;padding:12px 14px;margin:0 0 18px;font-size:13px;"><strong>Questions before buying?</strong> Use eBay&rsquo;s &ldquo;Ask seller a question&rdquo; on this listing. Once a sale completes, the item is accepted as shown in the photos.</div><p style="margin:0;font-size:12px;color:#aaa;text-align:center;">Description text defines vocabulary only. Packaging, shipping, returns, and any seller-specific guarantees are set by the seller in the eBay listing fields above. &middot; Prepared with CardSimp.</p>`;
}

// ============ Public entry point ============

export function composeDescription(item: ComposerItem): string {
  const bucket = detectBucket(item);
  const qty = Math.max(1, item.quantity ?? 1);

  const parts: string[] = [
    renderCreditStrip(),
    renderHeader(item),
    renderSpecsTable(bucket, item),
    renderSellerNote(item),
    renderIntro(bucket, item),
    renderMultiUnitDisclaimer(bucket, qty),
    renderConditionVocab(bucket),
    renderSingleCardDisclosure(bucket),
    renderGradedDisclosure(bucket),
    renderSealedDisclosure(bucket),
    renderLotDisclosure(bucket),
    renderFooter(),
  ].filter(Boolean);

  return `<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;max-width:780px;margin:0 auto;color:#1a1a1a;line-height:1.55;font-size:15px;">${parts.join('')}</div>`;
}
